import { Button, Card, Modal, Space, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingState } from "../components/LoadingState";
import { useApi } from "../hooks/useApi";
import api from "../services/api";
import type { User } from "../types";

export default function Home(): JSX.Element {
  const { t } = useTranslation();
  const [helloMessage, setHelloMessage] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const { loading, error, callApi } = useApi();

  const fetchHello = useCallback(async (): Promise<void> => {
    try {
      const data = await callApi(() => api.getHello());
      if (data?.message) {
        setHelloMessage(data.message);
      }
    } catch (err) {
      message.error(t("home.failedHello", { message: (err as Error).message }));
    }
  }, [callApi, t]);

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      const data = await callApi<User[]>(() => api.getUsers());
      if (data) {
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      message.error(t("home.failedUsers", { message: (err as Error).message }));
    }
  }, [callApi, t]);

  useEffect(() => {
    fetchHello();
    fetchUsers();
  }, [fetchHello, fetchUsers]);

  const columns: ColumnsType<User> = [
    {
      title: t("home.id"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("home.username"),
      dataIndex: "username",
      key: "username",
    },
    {
      title: t("home.email"),
      dataIndex: "email",
      key: "email",
    },
  ];

  if (loading) return <LoadingState />;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={t("home.apiTestTitle")}>
        {error ? (
          <div style={{ color: "red" }}>{error}</div>
        ) : (
          <p>{helloMessage || t("home.noMessage")}</p>
        )}
        <Button type="primary" onClick={fetchHello} loading={loading}>
          {t("home.refreshHello")}
        </Button>
      </Card>

      <Card title={t("home.usersTitle")}>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={loading}
          onRow={(record) => ({
            onClick: () => {
              Modal.info({
                title: t("home.userDetails", { username: record.username }),
                content: (
                  <div>
                    <p>
                      <strong>{t("home.id")}:</strong> {record.id}
                    </p>
                    <p>
                      <strong>{t("home.username")}:</strong> {record.username}
                    </p>
                    <p>
                      <strong>{t("home.email")}:</strong> {record.email}
                    </p>
                    <p>
                      <strong>{t("home.created")}:</strong>{" "}
                      {new Date(record.created_at).toLocaleString()}
                    </p>
                    <p>
                      <strong>{t("home.updated")}:</strong>{" "}
                      {new Date(record.updated_at).toLocaleString()}
                    </p>
                  </div>
                ),
              });
            },
          })}
        />
        <Button type="primary" onClick={fetchUsers} style={{ marginTop: 16 }} loading={loading}>
          {t("home.refreshUsers")}
        </Button>
      </Card>
      <Card title={t("home.testCardTitle")}>
        <p>{t("home.testCardBody")}</p>
      </Card>
      <Card title={t("home.helloWorldTitle")}>
        <p>{t("home.testCardBody")}</p>
      </Card>
    </Space>
  );
}
